import { z } from "zod";

/**
 * CTA button color palette -- reuses the same ten-value enum
 * as backgroundStyle so tenants pick from a familiar palette.
 */
export const ctaColorEnum = z.enum([
  "primary", "secondary", "tertiary", "dark", "brand", "light",
  "surface", "primaryBg", "secondaryBg", "tertiaryBg",
]);

/**
 * Per-slot Tailwind classes for CTA buttons.
 *
 * bg:        background class for the solid button
 * textOn:    text class for text ON the solid button (white for dark, text-text for light)
 * selfColor: text/border class in the slot's own color (for outline/ghost/inverted)
 * isDark:    whether the slot is dark (determines inverted logic)
 */
export const CTA_COLORS: Record<string, { bg: string; textOn: string; selfColor: string; isDark: boolean }> = {
  primary:     { bg: "bg-primary",      textOn: "text-white", selfColor: "text-primary",      isDark: true },
  secondary:   { bg: "bg-secondary",    textOn: "text-white", selfColor: "text-secondary",    isDark: true },
  tertiary:    { bg: "bg-tertiary",     textOn: "text-white", selfColor: "text-tertiary",     isDark: true },
  dark:        { bg: "bg-accent",       textOn: "text-white", selfColor: "text-accent",       isDark: true },
  brand:       { bg: "bg-background",   textOn: "text-white", selfColor: "text-background",   isDark: true },
  primaryBg:   { bg: "bg-primary-bg",   textOn: "text-white", selfColor: "text-primary-bg",   isDark: true },
  surface:     { bg: "bg-surface",      textOn: "text-text",  selfColor: "text-surface",      isDark: false },
  light:       { bg: "bg-white",        textOn: "text-text",  selfColor: "text-white",        isDark: false },
  secondaryBg: { bg: "bg-secondary-bg", textOn: "text-text",  selfColor: "text-secondary-bg", isDark: false },
  tertiaryBg:  { bg: "bg-tertiary-bg",  textOn: "text-text",  selfColor: "text-tertiary-bg",  isDark: false },
};

/**
 * Generate Tailwind classes for a CTA button given a palette slot, style, and
 * whether it is rendered in an inverted context (e.g. on a highlighted card or
 * dark-image hero).
 */
export function ctaClasses(slot: string = "primary", style: string = "solid", inverted: boolean = false): string {
  const c = CTA_COLORS[slot] || CTA_COLORS.primary;

  if (inverted) {
    // On a highlighted/dark-image card: white bg, slot-colored text
    return c.isDark
      ? `bg-white ${c.selfColor}`
      : `${c.bg} text-white`;
  }

  switch (style) {
    case "outline":
      return `bg-transparent border-2 ${c.bg.replace("bg-", "border-")} ${c.selfColor}`;
    case "ghost":
      return `bg-transparent ${c.selfColor} underline-offset-4 hover:underline hover:opacity-80`;
    default: // solid
      return `${c.bg} ${c.textOn} hover:opacity-90`;
  }
}
